/**
 * Analytics Hook - Track user events across the application
 * Captures: page views, product views, searches, filters, downloads, login, etc.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Get browser/device info
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const isMobile = /Mobile|Android|iPhone|iPad/.test(ua);
  const browser = /Chrome/.test(ua) ? 'Chrome' : 
                  /Firefox/.test(ua) ? 'Firefox' : 
                  /Safari/.test(ua) ? 'Safari' : 
                  /Edge/.test(ua) ? 'Edge' : 'Other';
  return { isMobile, browser, userAgent: ua };
};

// Get and cache user's country (using free IP geolocation API)
let cachedCountry = null;
let cachedCity = null;
const getUserCountry = async () => {
  if (cachedCountry) return { country: cachedCountry, city: cachedCity };
  
  try {
    // Try ipapi.co first (free, no key required, 1000 requests/day)
    const response = await fetch('https://ipapi.co/json/', { timeout: 3000 });
    if (response.ok) {
      const data = await response.json();
      cachedCountry = data.country_name || data.country || null;
      cachedCity = data.city || null;
      return { country: cachedCountry, city: cachedCity };
    }
  } catch (e) {
    console.log('Geolocation API unavailable');
  }
  
  return { country: null, city: null };
};

export const useAnalytics = () => {
  const sessionId = useRef(getSessionId());
  const deviceInfo = useRef(getDeviceInfo());
  const [geoData, setGeoData] = useState({ country: null, city: null });

  // Fetch geolocation on mount
  useEffect(() => {
    getUserCountry().then(setGeoData);
  }, []);

  // Get current user info from localStorage
  const getUserInfo = useCallback(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return {
          customer_id: user.id || null,
          customer_email: user.email || null,
          is_admin: user.is_admin || false,
        };
      }
    } catch (e) {}
    return { customer_id: null, customer_email: null, is_admin: false };
  }, []);

  const trackEvent = useCallback(async (eventType, data = {}) => {
    try {
      const userInfo = getUserInfo();
      
      // Skip tracking for admins - backend will also filter them
      if (userInfo.is_admin) {
        return;
      }
      
      const payload = {
        event_type: eventType,
        session_id: sessionId.current,
        customer_id: userInfo.customer_id,
        customer_email: userInfo.customer_email,
        user_agent: deviceInfo.current.userAgent,
        referrer: document.referrer || null,
        country: geoData.country,
        city: geoData.city,
        extra_data: {
          ...data.extra_data,
          device: deviceInfo.current.isMobile ? 'mobile' : 'desktop',
          browser: deviceInfo.current.browser,
          url: window.location.pathname,
          timestamp_local: new Date().toISOString(),
        },
        ...data,
      };

      // Remove nested extra_data from main payload if exists in data
      if (data.extra_data) {
        payload.extra_data = { ...payload.extra_data, ...data.extra_data };
      }

      // Fire and forget - don't block UI
      fetch(`${API_URL}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch (e) {}
  }, [getUserInfo, geoData]);

  // Track page view
  const trackPageView = useCallback((page, extraData = {}) => {
    trackEvent('page_view', { page, extra_data: extraData });
  }, [trackEvent]);

  // Track product view with full details
  const trackProductView = useCallback((product, extraData = {}) => {
    trackEvent('product_view', { 
      product_id: product.id, 
      product_name: product.website,
      extra_data: {
        country: product.country,
        category: product.category,
        price: product.pvp,
        dr: product.dr,
        ...extraData
      }
    });
  }, [trackEvent]);

  // Track search with results
  const trackSearch = useCallback((query, resultsCount = null, filters = {}) => {
    trackEvent('search', { 
      search_query: query, 
      extra_data: { 
        results_count: resultsCount,
        active_filters: filters
      } 
    });
  }, [trackEvent]);

  // Track filter usage with details
  const trackFilter = useCallback((filterName, filterValue, allFilters = {}) => {
    trackEvent('filter', { 
      extra_data: { 
        filter_name: filterName, 
        filter_value: filterValue,
        all_active_filters: allFilters
      } 
    });
  }, [trackEvent]);

  // Track download/export with full details (Excel, CSV, etc.)
  const trackDownload = useCallback((downloadType, details = {}) => {
    trackEvent('download', { 
      download_type: downloadType, // 'excel', 'csv', 'pdf'
      download_filename: details.filename || null,
      filter_params: details.filters || {},
      extra_data: { 
        type: downloadType,
        format: details.format || 'xlsx',
        products_count: details.count || 0,
        filters_used: details.filters || {},
        source: details.source || 'catalog', // 'catalog', 'admin', 'report'
        ...details 
      } 
    });
  }, [trackEvent]);

  // Track login with user details
  const trackLogin = useCallback((user = null) => {
    trackEvent('login', { 
      customer_id: user?.id || null, 
      customer_email: user?.email || null,
      extra_data: {
        is_admin: user?.is_admin || false,
        login_method: 'email'
      }
    });
  }, [trackEvent]);

  // Track logout
  const trackLogout = useCallback(() => {
    trackEvent('logout', {});
  }, [trackEvent]);

  // Track registration
  const trackRegister = useCallback((email) => {
    trackEvent('register', { 
      customer_email: email,
      extra_data: { registration_method: 'email' }
    });
  }, [trackEvent]);

  // Track button/action clicks
  const trackClick = useCallback((buttonName, context = {}) => {
    trackEvent('click', { 
      extra_data: { 
        button: buttonName,
        ...context 
      } 
    });
  }, [trackEvent]);

  // Track add to cart / order creation
  const trackAddToCart = useCallback((product, quantity = 1) => {
    trackEvent('add_to_cart', { 
      product_id: product.id,
      product_name: product.website,
      extra_data: {
        price: product.pvp,
        quantity,
        country: product.country,
        category: product.category
      }
    });
  }, [trackEvent]);

  // Track order creation
  const trackOrderCreate = useCallback((order) => {
    trackEvent('order_create', { 
      extra_data: {
        order_id: order.id,
        order_number: order.order_number,
        total_amount: order.total_amount,
        products_count: order.products?.length || 1
      }
    });
  }, [trackEvent]);

  // Track errors
  const trackError = useCallback((errorType, errorMessage, context = {}) => {
    trackEvent('error', { 
      extra_data: {
        error_type: errorType,
        error_message: errorMessage,
        ...context
      }
    });
  }, [trackEvent]);

  // Track time on page (call on unmount)
  const trackTimeOnPage = useCallback((page, startTime) => {
    const duration = Math.round((Date.now() - startTime) / 1000);
    trackEvent('time_on_page', { 
      page,
      extra_data: { duration_seconds: duration }
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackProductView,
    trackSearch,
    trackFilter,
    trackDownload,
    trackLogin,
    trackLogout,
    trackRegister,
    trackClick,
    trackAddToCart,
    trackOrderCreate,
    trackError,
    trackTimeOnPage,
    sessionId: sessionId.current,
    userCountry: geoData.country,
    userCity: geoData.city,
  };
};

// Auto-track page views on route change
export const usePageTracking = (pageName) => {
  const { trackPageView, trackTimeOnPage } = useAnalytics();
  const startTime = useRef(Date.now());
  
  useEffect(() => {
    trackPageView(pageName);
    startTime.current = Date.now();
    
    return () => {
      trackTimeOnPage(pageName, startTime.current);
    };
  }, [pageName, trackPageView, trackTimeOnPage]);
};

export default useAnalytics;
